'use client';
import { useState } from 'react';
import { isMatchMediaAvailable } from '@s7/styles';
import { arrayOf, isFunction } from '@s7/utils';
import { useExtendedQueries } from './useExtendedQuery/useExtendedQueries.js';
import { useIsomorphicEffect } from '../useIsomorphicEffect/useIsomorphicEffect.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
var completeRight = function(value, fallback, length) {
    return length ? [].concat(value, Array(length).fill(fallback)) : value;
};
/**
 * @param {PolymorphicQuery | PolymorphicQuery[]} queries Один медиа-запрос или массив медиа-запросов в расширенном синтаксисе.
 * @param {UseMediaQueryOptions} options Параметры ssr, fallback
 * @param {boolean[]} options.fallback То, что будет возвращено, если для ssr установлено значение true.
 * @param {boolean} options.ssr Определяет, следует ли вернуть fallback или выполнить запрос напрямую.
 *
 * Поддерживает расширенный синтаксис запросов:
 *
 * @example
 * const [xs1, sm1, md1Up, ...others] = useMediaQuery([
 *    'xs1',            // (>= xs1)
 *    'sm1',            // (>= xs1)
 *    'md1.up',         // (>= md1)
 *    'md1.down',       // (< md1)
 *    '1024.down',      // (< 1024)
 *    '[lg1,xl1]',      // (>= lg1) & (< xl1)
 *    '[lg1,1024]',     // (>= lg1) & (< 1024)
 *    '[30em,1024]',    // (>= 30em) & (< 1024)
 *    '(display-mode: browser)',
 * ]);
 *
 * @return boolean[]
 * */ var useMediaQuery = function(queries, options) {
    var _ref = options || {}, _ref_ssr = _ref.ssr, ssr = _ref_ssr === void 0 ? true : _ref_ssr, fallback = _ref.fallback;
    var raws = useExtendedQueries(queries);
    var _useState = useState(function() {
        return raws.map(function(query, index) {
            return {
                media: query,
                matches: ssr ? completeRight(arrayOf(fallback != null ? fallback : false), false, queries.length)[index] : window.matchMedia(query).matches
            };
        });
    }), matches = _useState[0], setMatches = _useState[1];
    var handleUpdate = function(event) {
        setMatches(function(prev) {
            return prev.slice().map(function(item) {
                if (item.media === event.media) {
                    return _extends({}, item, {
                        matches: event.matches
                    });
                }
                return item;
            });
        });
    };
    useIsomorphicEffect(function() {
        if (isMatchMediaAvailable()) {
            var mql = raws.map(function(query) {
                return window.matchMedia(query);
            });
            setMatches(mql.map(function(entry) {
                return {
                    matches: entry.matches,
                    media: entry.media
                };
            }));
            /**
             * Версии Safari, поставляемые с Catalina и более ранними версиями, не поддерживают addEventListener на matchMedia
             * https://stackoverflow.com/questions/56466261/matchmedia-addlistener-marked-as-deprecated-addeventlistener-equivalent
             * */ mql.forEach(function(mql) {
                if (isFunction(mql.addListener)) {
                    mql.addListener(handleUpdate);
                } else {
                    mql.addEventListener("change", handleUpdate);
                }
            });
            return function() {
                mql.forEach(function(mql) {
                    if (isFunction(mql.removeListener)) {
                        mql.removeListener(handleUpdate);
                    } else {
                        mql.removeEventListener("change", handleUpdate);
                    }
                });
            };
        }
        return undefined;
    }, []);
    return matches.map(function(entry) {
        return entry.matches;
    });
};

export { useMediaQuery };
